import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { baseUrl } from 'api/config'
import axios from 'axios'

// ── Types ────────────────────────────────────────────────────────────────────

export interface FormField {
  fieldId: string
  label: string
  type: 'text' | 'textarea' | 'select' | 'multiselect' | 'number' | 'date' | 'email' | 'url' | 'checkbox'
  required: boolean
  placeholder?: string
  options?: string[]
  defaultValue?: string
  helpText?: string
  clickUpFieldId?: string
  clickupFieldType?: string
  clickupMapping?: 'none' | 'description' | 'due_date' | 'custom_field'
  clickupOptions?: { label: string; value: string; clickupOptionId: string }[]
  conditionalRule?: {
    sourceFieldKey: string
    operator: 'equals' | 'not_equals' | 'contains' | 'not_empty' | 'is_empty'
    value?: string
    action: 'show' | 'hide'
  }
}

export interface ClickUpTarget {
  spaceId?: string
  folderId?: string
  listId?: string
}

export interface FormDefinition {
  PK: string
  SK: string
  formId: string
  name: string
  description: string
  fields: FormField[]
  createdBy: string
  createdAt: string
  updatedAt: string
  status: 'active' | 'draft' | 'archived'
  submitCount: number
  clickUpTarget?: ClickUpTarget | null
  // ClickUp integration
  clickupEnabled?: boolean
  clickupSpaceId?: string
  clickupListPattern?: string
  clickupTargetListId?: string
  clickupTargetListName?: string
  clickupAssigneeId?: number
  clickupAssigneeName?: string
  descriptionTemplate?: string
  completionMessage?: string
}

export interface FormSubmission {
  PK: string
  SK: string
  submissionId: string
  formId: string
  responses: Record<string, any>
  submittedAt: string
  submittedBy?: string | null
  clickUpTaskId?: string | null
}

// ── Config ───────────────────────────────────────────────────────────────────

const BASE = `${baseUrl}/api/form-builder`
const KEY = ['form-builder']

// ── Form CRUD ────────────────────────────────────────────────────────────────

export const useFormList = () =>
  useQuery<FormDefinition[]>({
    queryKey: KEY,
    queryFn: () => axios.get<FormDefinition[]>(BASE).then(r => r.data),
    staleTime: 1000 * 60 * 2,
  })

export const useForm = (id: string | null) =>
  useQuery<FormDefinition>({
    queryKey: [...KEY, id],
    queryFn: () => axios.get<FormDefinition>(`${BASE}/${id}`).then(r => r.data),
    enabled: !!id,
    staleTime: 1000 * 60,
  })

export const useCreateForm = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<FormDefinition>) =>
      axios.post<FormDefinition>(BASE, data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export const useUpdateForm = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Partial<FormDefinition>) =>
      axios.put<FormDefinition>(`${BASE}/${id}`, data).then(r => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEY })
      qc.invalidateQueries({ queryKey: [...KEY, vars.id] })
    },
  })
}

export const useDeleteForm = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => axios.delete(`${BASE}/${id}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

// ── Submissions ──────────────────────────────────────────────────────────────

export const useFormSubmissions = (formId: string | null) =>
  useQuery<FormSubmission[]>({
    queryKey: [...KEY, formId, 'submissions'],
    queryFn: () => axios.get<FormSubmission[]>(`${BASE}/${formId}/submissions`).then(r => r.data),
    enabled: !!formId,
    staleTime: 1000 * 30,
  })

export const useSubmitForm = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ formId, ...data }: {
      formId: string
      responses: Record<string, any>
      submittedBy?: string
      listId?: string
      taskName?: string
      description?: string
    }) =>
      axios.post<FormSubmission & { clickUpTask?: { id: string; name: string; url: string } }>(`${BASE}/${formId}/submit`, data).then(r => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: [...KEY, vars.formId, 'submissions'] })
      qc.invalidateQueries({ queryKey: KEY })
    },
  })
}

// ── ClickUp Field Picker ────────────────────────────────────────────────────

export interface ClickUpFieldOption {
  id: string
  name: string
  label: string
  color?: string
}

export interface ClickUpCustomField {
  id: string
  name: string
  type: string
  options: ClickUpFieldOption[]
}

export const useClickUpFieldsForForm = (formId: string | null, listId: string | null) =>
  useQuery<ClickUpCustomField[]>({
    queryKey: [...KEY, formId, 'clickup-fields', listId],
    queryFn: () => axios.get<ClickUpCustomField[]>(`${BASE}/${formId}/clickup-fields/${listId}`).then(r => r.data),
    enabled: !!formId && !!listId,
    staleTime: 5 * 60_000,
  })
